import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import apiClient from '../api/axios';
import { Spin } from 'antd';

export interface SiteSettings {
    [key: string]: string;
}

interface ThemeContextType {
    siteTokens: SiteSettings;
    themeTokens: SiteSettings;
    loading: boolean;
    refreshSettings: () => Promise<void>;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [siteTokens, setSiteTokens] = useState<SiteSettings>({});
    const [themeTokens, setThemeTokens] = useState<SiteSettings>({});
    const [loading, setLoading] = useState(true);

    const fetchSettings = async () => {
        try {
            const response = await apiClient.get('/public/site-settings');
            if (response.data && response.data.succeeded) {
                const { siteTokens: st, themeTokens: tt } = response.data.data;
                setSiteTokens(st || {});
                setThemeTokens(tt || {});

                // Inject Theme Tokens into CSS Variables
                const root = document.documentElement;
                Object.entries(tt || {}).forEach(([key, value]) => {
                    root.style.setProperty(key, value as string);
                });

                // Update Favicon dynamically
                if (st && st.FaviconUrl) {
                    let link: HTMLLinkElement | null = document.querySelector("link[rel~='icon']");
                    if (!link) {
                        link = document.createElement('link');
                        link.rel = 'icon';
                        document.head.appendChild(link);
                    }
                    link.href = st.FaviconUrl;
                }
            }
        } catch (error) {
            console.error('Failed to fetch site settings', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSettings();
    }, []);

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: 'var(--bg-color, #f0f2f5)' }}>
                <Spin size="large" description="Loading application settings..." />
            </div>
        );
    }

    return (
        <ThemeContext.Provider value={{ siteTokens, themeTokens, loading, refreshSettings: fetchSettings }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useThemeSettings = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useThemeSettings must be used within a ThemeProvider');
    }
    return context;
};
